import datapoints from './datapoints';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import deleteConversationStoreUpdate from 'owa-mail-actions/lib/triage/deleteConversationStoreUpdate';
import { getApplyConversationActionContext, getRowIdsFromRowKeys } from 'owa-mail-list-store';
import { getStore } from 'owa-mail-list-store/lib/store/Store';
import filterConversationRowsByRestriction from 'owa-mail-list-store/lib/utils/filterConversationRowsByRestriction';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import { getApolloClient } from 'owa-apollo';
import { ApplyConversationActionContext } from 'owa-mail-triage-action-utils';
import getEwsRequestDateString from 'owa-mail-triage-action-utils/lib/getEwsRequestDateString';
import {
    UndoDeleteConversationDocument,
    UndoDeleteConversationMutation,
} from '../graphql/__generated__/undoDeleteConversationMutation.interface';

type UndoDeleteConversationResult = UndoDeleteConversationMutation['undoDeleteConversation'];

/**
 * Restore conversations action
 * @param conversationRowKeys the specified rowKeys
 * @param tableViewId the table view id
 * @return the action response
 */
export default createLegacyActionWithDatapoint(
    'RestoreConversation',
    datapoints.restoreConversation
)(function restoreConversations(conversationRowKeys: string[], tableViewId: string): Promise<void> {
    const tableView = getStore().tableViews.get(tableViewId);
    const filteredRowKeys = filterConversationRowsByRestriction(
        conversationRowKeys,
        tableViewId,
        tableConversationRelation => !!tableConversationRelation
    );

    const conversationIdStrings = getRowIdsFromRowKeys(filteredRowKeys, tableViewId);
    const conversationActionContexts = filteredRowKeys.map(rowKey =>
        getApplyConversationActionContext(rowKey, tableViewId)
    );

    // 1. Make local update
    deleteConversationStoreUpdate(conversationIdStrings, tableView, 'Restore');

    // 2. Make service request
    // When restoring conversation we should act upon folderId and not on serverFolderId
    // so that entire conversation and not just the items belonging to the serverFolderId gets restored
    const contextFolderId = tableView.tableQuery.folderId;
    const restoreConversationPromise = exportedHelperFunctions.invokeUndoDeleteMutation(
        conversationActionContexts,
        contextFolderId
    );

    return processApplyConversationActionResponseMessage(
        restoreConversationPromise,
        filteredRowKeys,
        tableView.serverFolderId,
        tableView,
        false /* shouldRemoveRowsFromTombstone */,
        true /* skipCheckingResponseMessage */
    );
});

/**
 * Invoke the UndoDeleteConversation graphql mutation
 * @param conversationContexts the conversation contexts to be deleted
 * @param folderId the folder id
 * @param undoRequestId requestId for undo needed in case of Hx
 * @return the ApplyConversationActionResponseMessage
 */
const invokeUndoDeleteMutationFn = async function invokeUndoDeleteMutation(
    conversationContexts: ApplyConversationActionContext[],
    folderId: string,
    undoRequestId?: number
): Promise<UndoDeleteConversationResult> {
    const conversationSnapShots = conversationContexts.map(context => {
        return {
            id: context.conversationId,
            messagesAsOf: getEwsRequestDateString(context.conversationLastSyncTimeStamp),
        };
    });
    const client = await getApolloClient();
    const result = await client.mutate({
        variables: {
            conversations: conversationSnapShots,
            folderId: folderId,
            undoRequestId: undoRequestId,
        },
        mutation: UndoDeleteConversationDocument,
    });

    return result?.data?.undoDeleteConversation;
};

export const exportedHelperFunctions = {
    invokeUndoDeleteMutation: invokeUndoDeleteMutationFn,
};
